import React, { useState } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { Checkbox } from '@/components/ui/checkbox';
import { CustomRangeSlider } from '@/components/ui/custom-range-slider';
import { CustomSlider } from '@/components/ui/custom-slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertModal } from '@client/components/AlertModal';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { useFormContext } from 'react-hook-form';

import { useShopInsuranceContext, type PaymentOption } from '@/components/ShopInsuranceModal';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { DEFAULT_FILTERS_VALUES, type CombinedFilterType } from '@client/hooks/useAdjustedQuotes';
import { formatCurrency } from '@client/lib';
import { selectClient } from '@client/model';
import { selectFeatureFlags } from '@client/model/selectors';
import { UNDERWRITING_STATUS_FILTERS, zipCodeToState } from '@shared';
import { Info } from 'lucide-react';
import { useSelector } from 'react-redux';

type NoticeType = 'inflationProtection' | 'benefitDuration' | null;

interface ShopInsuranceFiltersProps {
  selectedEligibilityFilters?: string[];
  setSelectedEligibilityFilters?: (filters: string[]) => void;
  showEligibilityFilter?: boolean;
  eligibilityCounts?: {
    eligible: number;
    unsure: number;
    ineligible: number;
  };
  selectedFeatureBenefitsFilters?: string[];
  setSelectedFeatureBenefitsFilters?: (filters: string[]) => void;
}

export function ShopInsuranceFilters({
  selectedEligibilityFilters = [],
  setSelectedEligibilityFilters,
  showEligibilityFilter = true,
  eligibilityCounts,
  selectedFeatureBenefitsFilters = [],
  setSelectedFeatureBenefitsFilters,
}: ShopInsuranceFiltersProps = {}) {
  const featureFlags = useSelector(selectFeatureFlags);
  const { flags } = useFeatureFlags();
  const client = useSelector(selectClient);
  const state = zipCodeToState(client.intakeSurvey.clientZipCode);
  const [showNotice, setShowNotice] = useState(false);
  const [noticeType, setNoticeType] = useState<NoticeType>(null);
  const { setValue, watch, control, resetField, getValues } = useFormContext();
  const {
    sliderBounds,
    paymentOption,
    handlePaymentOptionChange,
    showTraditionalLTCOptions,
    showSinglePayAnd10Pay,
  } = useShopInsuranceContext();

  const premiumValue = watch('premium');
  const benefitDurationValue: CombinedFilterType = watch('benefitDuration');
  const inflationProtectionValue: CombinedFilterType = watch('inflationProtection');

  // Clamp the displayed slider value to min/max bounds
  const clampedPremiumValue = Math.max(sliderBounds.min, Math.min(sliderBounds.max, premiumValue));

  const showFilters = !['Monthly', 'Annually'].includes(paymentOption);

  function handleNoticeChange(type: NoticeType) {
    const inflationProtectionNotice = JSON.parse(
      localStorage.getItem('inflationProtectionNotice') || 'false',
    );
    const benefitDurationNotice = JSON.parse(
      localStorage.getItem('benefitDurationNotice') || 'false',
    );

    if (
      (type === 'benefitDuration' && !benefitDurationNotice) ||
      (type === 'inflationProtection' && !inflationProtectionNotice)
    ) {
      setNoticeType(type);
      setShowNotice(true);
    } else {
      setNoticeType(null);
      setShowNotice(false);
    }
  }

  function handleConfirm(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();

    setValue('benefitDuration', getValues('benefitDuration'));
    setValue('inflationProtection', getValues('inflationProtection'));

    if (noticeType === 'benefitDuration') {
      localStorage.setItem('benefitDurationNotice', 'true');
    } else if (noticeType === 'inflationProtection') {
      localStorage.setItem('inflationProtectionNotice', 'true');
    }

    setShowNotice(false);
  }

  function handleOnChange(value: boolean) {
    if (!value) {
      if (noticeType === 'benefitDuration') {
        resetField('benefitDuration');
      } else if (noticeType === 'inflationProtection') {
        resetField('inflationProtection');
      }
    }

    setShowNotice(value);
  }

  const eligibilityFilters = UNDERWRITING_STATUS_FILTERS;

  const getEligibilityTooltipContent = (filterValue: string) => {
    switch (filterValue) {
      case 'eligible':
        return 'Products available to applicants who are likely to qualify based on their health status';
      case 'unsure':
        return 'Products that may require additional health review or underwriting';
      case 'ineligible':
        return 'Products likely are not available because applicants do not meet carriers’ health requirements';
      default:
        return '';
    }
  };

  const InfoTooltip = ({ content }: { content: React.ReactNode }) => (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-gray-500 transition-colors hover:text-gray-700"
          >
            <Info className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipPrimitive.Portal>
          <TooltipContent className="m-1 w-auto max-w-80 whitespace-normal border border-gray-300 bg-white p-2 text-sm font-normal shadow-md">
            {content}
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </Tooltip>
    </TooltipProvider>
  );

  const handleEligibilityFilterToggle = (value: string) => {
    if (!setSelectedEligibilityFilters) return;

    const newFilters = selectedEligibilityFilters.includes(value)
      ? selectedEligibilityFilters.filter(filter => filter !== value)
      : [...selectedEligibilityFilters, value];
    setSelectedEligibilityFilters(newFilters);
  };

  const handleFeatureBenefitsFilterToggle = (value: string) => {
    if (!setSelectedFeatureBenefitsFilters) return;

    const newFilters = selectedFeatureBenefitsFilters.includes(value)
      ? selectedFeatureBenefitsFilters.filter(filter => filter !== value)
      : [...selectedFeatureBenefitsFilters, value];
    setSelectedFeatureBenefitsFilters(newFilters);
  };

  return (
    <div className="space-y-6">
      {showFilters && (
        <div>
          <h3 className="text-primary-dark mb-4 text-sm font-medium">Target Premium</h3>
          <div className="px-2 space-y-3">
            <CustomSlider
              name="premium"
              control={control}
              max={sliderBounds.max}
              min={sliderBounds.min}
              step={1000}
              defaultValue={sliderBounds.defaultValue}
              formatValue={value => formatCurrency(value)}
              hideResetButton={false}
              defaultLabel="Reset"
              label={formatCurrency(clampedPremiumValue)}
              showCustomInput={flags.customPremiumInput ?? false}
              customInputLabel="Enter custom amount"
              data-testid="shop-insurance-filters-premium-min"
            />
          </div>
        </div>
      )}
      <div className={showFilters ? '' : ''}>
        <h3 className="text-primary-dark mb-3 text-sm font-medium">Payment Option</h3>
        <Select
          value={paymentOption}
          onValueChange={value => handlePaymentOptionChange(value as PaymentOption)}
        >
          <SelectTrigger
            className="w-full rounded-lg text-gray-900"
            data-testid={`shop-insurance-filters-payment-option`}
          >
            <SelectValue placeholder="Select payment option" />
          </SelectTrigger>
          <SelectContent>
            {showSinglePayAnd10Pay && (
              <>
                <SelectItem value="Single Pay">Single Pay</SelectItem>
                <SelectItem value="10-Pay">10-Pay</SelectItem>
              </>
            )}
            {showTraditionalLTCOptions && (
              <>
                <SelectItem value="Annually">Annually</SelectItem>
                <SelectItem value="Monthly">Monthly</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </div>
      {showFilters && (
        <div className="border-t border-gray-200 pt-6">
          <div className="mb-4 flex items-center justify-between gap-2">
            <h3 className="text-primary-dark text-sm font-medium">Inflation Protection</h3>
            <Select
              value={inflationProtectionValue.type}
              onValueChange={value =>
                setValue('inflationProtection', { ...inflationProtectionValue, type: value })
              }
            >
              <SelectTrigger
                data-testid="shop-insurance-filters-inflation-protection-select"
                className="h-max w-max px-1.5 py-1 text-xs text-gray-900 [&>svg]:size-3"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="combined">Combined</SelectItem>
                <SelectItem value="separate">Separate AOB/COB</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {inflationProtectionValue.type === 'combined' ? (
            <CustomRangeSlider
              showTooltip
              min={0}
              max={5}
              step={1}
              control={control}
              name="inflationProtection.combined"
              tooltipContent={value => `${value}%`}
              defaultValue={DEFAULT_FILTERS_VALUES.inflationProtection}
              onValueChange={() => handleNoticeChange('inflationProtection')}
              label={`${inflationProtectionValue.combined[0]}% - ${inflationProtectionValue.combined[1]}%`}
              testId="shop-insurance-filters-inflation-protection-combined"
            />
          ) : (
            <div className="flex w-full gap-4">
              <div className="w-full">
                <h4 className="text-primary-dark mb-2 text-xs font-medium">AOB</h4>
                <CustomRangeSlider
                  showTooltip
                  min={0}
                  max={5}
                  step={1}
                  size="sm"
                  control={control}
                  name="inflationProtection.aob"
                  tooltipContent={value => `${value}%`}
                  defaultValue={DEFAULT_FILTERS_VALUES.inflationProtection}
                  onValueChange={() => handleNoticeChange('inflationProtection')}
                  label={`${inflationProtectionValue.aob[0]}% - ${inflationProtectionValue.aob[1]}%`}
                  testId="shop-insurance-filters-inflation-protection-aob"
                />
              </div>
              <div className="w-full">
                <h4 className="text-primary-dark mb-2 text-xs font-medium">COB</h4>
                <CustomRangeSlider
                  showTooltip
                  min={0}
                  max={5}
                  step={1}
                  size="sm"
                  control={control}
                  name="inflationProtection.cob"
                  tooltipContent={value => `${value}%`}
                  defaultValue={DEFAULT_FILTERS_VALUES.inflationProtection}
                  onValueChange={() => handleNoticeChange('inflationProtection')}
                  label={`${inflationProtectionValue.cob[0]}% - ${inflationProtectionValue.cob[1]}%`}
                  testId="shop-insurance-filters-inflation-protection-cob"
                />
              </div>
            </div>
          )}
        </div>
      )}
      {showFilters && (
        <div className="border-t border-gray-200 pt-6">
          <div className="mb-4 flex items-center justify-between gap-2">
            <h3 className="text-primary-dark text-sm font-medium">Benefit Duration</h3>
            <Select
              value={benefitDurationValue.type}
              onValueChange={value =>
                setValue('benefitDuration', { ...benefitDurationValue, type: value })
              }
            >
              <SelectTrigger className="h-max w-max px-1.5 py-1 text-xs text-gray-900 [&>svg]:size-3">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="combined">Total Duration</SelectItem>
                <SelectItem value="separate">Separate AOB/COB</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {benefitDurationValue.type === 'combined' ? (
            <CustomRangeSlider
              showTooltip
              min={1}
              max={8}
              step={1}
              control={control}
              name="benefitDuration.combined"
              tooltipContent={value => `${value} Year${value !== 1 ? 's' : ''}`}
              defaultValue={DEFAULT_FILTERS_VALUES.benefitDuration}
              onValueChange={() => handleNoticeChange('benefitDuration')}
              label={`${benefitDurationValue.combined[0]} - ${benefitDurationValue.combined[1]} Years`}
              testId="shop-insurance-filters-benefit-duration-combined"
            />
          ) : (
            <div className="flex w-full gap-4">
              <div className="w-full">
                <h4 className="text-primary-dark mb-2 text-xs font-medium">AOB</h4>
                <CustomRangeSlider
                  showTooltip
                  size="sm"
                  min={1}
                  step={1}
                  max={8}
                  control={control}
                  name="benefitDuration.aob"
                  tooltipContent={value => `${value} Year${value !== 1 ? 's' : ''}`}
                  defaultValue={DEFAULT_FILTERS_VALUES.benefitDuration}
                  onValueChange={() => handleNoticeChange('benefitDuration')}
                  label={`${benefitDurationValue.aob[0]} - ${benefitDurationValue.aob[1]} Years`}
                  testId="shop-insurance-filters-benefit-duration-aob"
                />
              </div>
              <div className="w-full">
                <h4 className="text-primary-dark mb-2 text-xs font-medium">COB</h4>
                <CustomRangeSlider
                  size="sm"
                  showTooltip
                  min={1}
                  step={1}
                  max={8}
                  control={control}
                  name="benefitDuration.cob"
                  tooltipContent={value => `${value} Year${value !== 1 ? 's' : ''}`}
                  defaultValue={DEFAULT_FILTERS_VALUES.benefitDuration}
                  onValueChange={() => handleNoticeChange('benefitDuration')}
                  label={`${benefitDurationValue.cob[0]} - ${benefitDurationValue.cob[1]} Years`}
                  testId="shop-insurance-filters-benefit-duration-cob"
                />
              </div>
            </div>
          )}
        </div>
      )}
      {setSelectedEligibilityFilters && showEligibilityFilter && (
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-primary-dark mb-4 text-sm font-medium">Eligibility Filter</h3>
          <div className="flex flex-col gap-3">
            {eligibilityFilters.map(filter => {
              const count =
                eligibilityCounts?.[filter.value as keyof typeof eligibilityCounts] || 0;
              return (
                <div
                  key={filter.value}
                  className="flex items-center gap-3"
                >
                  <Checkbox
                    data-testid={`shop-insurance-filters-eligibility-${filter.value}`}
                    id={filter.value}
                    checked={selectedEligibilityFilters.includes(filter.value)}
                    onCheckedChange={() => handleEligibilityFilterToggle(filter.value)}
                    className="data-[state=checked]:bg-primary data-[state=checked]:border-primary h-5 w-5 rounded border-2 border-gray-400"
                  />
                  <label
                    htmlFor={filter.value}
                    className="flex flex-1 cursor-pointer items-center gap-2 text-sm font-medium text-gray-700"
                  >
                    {filter.label}
                    <span className="text-xs text-gray-700">({count})</span>
                  </label>
                  <InfoTooltip content={getEligibilityTooltipContent(filter.value)} />
                </div>
              );
            })}
          </div>
        </div>
      )}
      {featureFlags.quoteApplySecurianFinancial &&
        !['CA', 'NY'].includes(state.stateAbbreviation) && (
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-primary-dark mb-4 text-sm font-medium">Features & Benefits</h3>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  data-testid="shop-insurance-filters-global-benefits"
                  id="hasGlobalBenefits"
                  checked={selectedFeatureBenefitsFilters.includes('hasGlobalBenefits')}
                  onCheckedChange={() => handleFeatureBenefitsFilterToggle('hasGlobalBenefits')}
                  className="data-[state=checked]:bg-primary data-[state=checked]:border-primary h-5 w-5 rounded border-2 border-gray-400"
                />
                <label
                  htmlFor="hasGlobalBenefits"
                  className="flex flex-1 cursor-pointer items-center gap-2 text-sm font-medium text-gray-700"
                >
                  International Benefits
                </label>
              </div>
            </div>
          </div>
        )}
      <AlertModal
        title="Are you sure?"
        open={showNotice}
        onOpenChange={handleOnChange}
        onConfirm={handleConfirm}
      >
        By adjusting this filter you are reducing the number of policies analyzed, which may filter
        out policies with a high modeled ROI, leaving you with lower modeled ROI options. Are you
        sure you want to restrict the search?
      </AlertModal>
    </div>
  );
}
